/**
 * RQ-117 — ecrã-cópia do Automatic (isolamento FE).
 * Textos de menu são provisórios: um único sítio. Não usar em API/payload.
 * Screen id interno (F5) não é nome de produto.
 */
import { isPrivilegedAdmin, isSuperAdmin } from "../utils/roles";
import { createEmptyRegressionState } from "./valuationAutomaticRegressionContract_v1";
import { resolveComparableDisplayFields } from "./valuationAutomaticComparablesContract_v1";
import { SPATIAL_STEP_META } from "./valuationAutomaticSpatialContract_v1";
import { TIMELINE_STEPS } from "./valuationAutomaticBrand_v1";
import poiAllowlistSpCapital from "../data/poi_allowlist_sp_capital_v1.json";

export const AVM_SCREEN_AUTOMATIC_CLONE_RQ117 = "valuationAutomaticCloneRq117";
export const AUTOMATIC_CLONE_RQ117_LAB_ENABLED = true;

/** Só UI. Trocar quando esta cópia substituir o Automatic oficial. */
export const AUTOMATIC_LAB_COPY_UI = Object.freeze({
  menuLabel: "Avaliação Automatic",
  menuSubtitle: "Avaliação automática",
  banner: "",
  productName: "AVM Enterprise — Avaliação Automatic"
});

/** NBR legado: visível só ao Super Admin, fantasma, fora de uso. */
export const NBR_ARCHIVED_SCREEN_IDS = Object.freeze([
  "sisavalStudio",
  "valuationEnterprise",
  "valuationAutomatic"
]);

export function isNbrArchivedScreen(screenId) {
  return NBR_ARCHIVED_SCREEN_IDS.includes(String(screenId || ""));
}

export function canSeeNbrLegacyGhostMenus(user) {
  return isSuperAdmin(user);
}

export function asNbrGhostMenuItem(item) {
  if (!item) return null;
  const sub = String(item.subtitle || "").trim();
  return {
    ...item,
    ghost: true,
    muted: true,
    disabled: true,
    subtitle: sub ? `${sub} · não em uso` : "Não em uso"
  };
}

/** Frases visíveis a cliente/utilizador — tom profissional AVM Enterprise (RQ-129). */
export const LAB_CLIENT_COPY = Object.freeze({
  disclaimer:
    "Resultado calculado automaticamente com base nos dados desta avaliação. O responsável técnico utiliza essas informações na composição da avaliação.",
  confidence: "Estimativa calculada pelo modelo para os dados disponíveis nesta avaliação.",
  factsLead:
    "Dados utilizados no cálculo: características do imóvel avaliando, comparáveis da amostra, renda do setor e distâncias de referência do entorno.",
  presentationLead:
    "A política do modelo (Etapa 6) e o arbítrio do avaliador (Etapa 7), quando aplicados, entram no valor apresentado — em etapas distintas.",
  finalizeOk: "Avaliação finalizada."
});

/**
 * Linha curta das variáveis do sujeito que entram no GBM (área, tipología, quartos, banheiros, vagas).
 * Suítes no formulário alimentam o cadastro; no motor entram via dormitórios (quartos).
 */
export function formatLabSubjectFactsLine(subject = {}) {
  const s = subject || {};
  const parts = [];
  const tip = s.tipulo || s.tipologia || null;
  if (tip) parts.push(String(tip));
  const area = Number(s.areaPrivativa ?? s.area_privativa ?? s.area);
  if (Number.isFinite(area) && area > 0) parts.push(`${area} m²`);
  const quartos = Number(s.quartos ?? s.dormitorios);
  if (Number.isFinite(quartos) && quartos >= 0 && s.quartos != null) {
    parts.push(`${quartos} dorm.`);
  }
  if (s.suites != null && s.suites !== "") {
    const su = Number(s.suites);
    if (su === 1 || String(s.suites).toLowerCase() === "sim") parts.push("com suíte");
    else if (su === 0 || String(s.suites).toLowerCase() === "nao" || String(s.suites).toLowerCase() === "não") {
      parts.push("sem suíte");
    }
  }
  const ban = Number(s.banheiros);
  if (Number.isFinite(ban) && s.banheiros != null) parts.push(`${ban} banheiros`);
  const vag = Number(s.vagas);
  if (Number.isFinite(vag) && s.vagas != null) parts.push(`${vag} vagas`);
  if (!parts.length) return null;
  return parts.join(" · ");
}

/** Id técnico do motor lab (não é o nome do menu; não usar no Automatic oficial). */
export const AUTOMATIC_LAB_COPY_MOTOR = Object.freeze({
  modelVersion: "avaliaexpress_v1_core",
  engineVersion: "avaliaexpress_v1_core",
  defaultRadiusM: 2000,
  searchLimit: 300,
  kMax: 300
});

export function createLabCopyRegressionState() {
  return {
    ...createEmptyRegressionState(),
    modelVersion: AUTOMATIC_LAB_COPY_MOTOR.modelVersion,
    engineVersion: AUTOMATIC_LAB_COPY_MOTOR.engineVersion,
    offerFactorId: "offer_5",
    applyElasticity: false,
    elasticityRate: 0
  };
}

/**
 * RQ-127 — reaplica fator oferta no valor final sem re-ML.
 * Base = valor_modelo / vu_bruto (VU do modelo).
 * RQ-131 — nunca apagar R² / MAE / fit_points in-sample (não dependem da oferta).
 */
export function applyFinalOfferToResult(result, offerRate) {
  const r = result && typeof result === "object" ? { ...result } : {};
  const rate = Number(offerRate);
  const safeRate = Number.isFinite(rate) && rate >= 0 && rate < 1 ? rate : 0;
  const vuModelo = Number(
    r.vu_bruto != null ? r.vu_bruto : r.vu_modelo != null ? r.vu_modelo : r.vu_ajustado
  );
  const areaFromTotal =
    r.valor_modelo != null && Number.isFinite(vuModelo) && vuModelo > 0
      ? Number(r.valor_modelo) / vuModelo
      : r.valor_total_bruto != null && Number.isFinite(vuModelo) && vuModelo > 0
        ? Number(r.valor_total_bruto) / vuModelo
        : null;
  let valorModelo = Number(r.valor_modelo);
  if (!Number.isFinite(valorModelo) || valorModelo <= 0) {
    if (Number.isFinite(vuModelo) && vuModelo > 0 && Number.isFinite(areaFromTotal) && areaFromTotal > 0) {
      valorModelo = vuModelo * areaFromTotal;
    } else {
      valorModelo = Number(r.valor_total_bruto) || Number(r.valor_total) || null;
    }
  }
  if (!Number.isFinite(vuModelo) || vuModelo <= 0 || !Number.isFinite(valorModelo) || valorModelo <= 0) {
    return preserveInSampleMetrics(r, r);
  }
  const vuAdj = vuModelo * (1 - safeRate);
  const totalAdj = valorModelo * (1 - safeRate);
  return preserveInSampleMetrics(
    {
      ...r,
      vu_bruto: vuModelo,
      vu_ajustado: vuAdj,
      valor_unitario: vuAdj,
      valor_modelo: valorModelo,
      valor_total_bruto: valorModelo,
      valor_total: totalAdj,
      valor_central: totalAdj,
      offer_factor: {
        ...(r.offer_factor || {}),
        rate: safeRate,
        percent: Math.round(safeRate * 100),
        applied: safeRate > 0,
        mode: "final_value"
      },
      elasticity: { applied: false, rate: 0 }
    },
    r
  );
}

/**
 * RQ-131 — ao reescrever result (ex.: oferta na Etapa 6), manter métricas in-sample.
 * R²/MAE/fit_points não mudam com o fator oferta.
 */
export function preserveInSampleMetrics(next, donor) {
  if (!next || typeof next !== "object") return next;
  const d = donor && typeof donor === "object" ? donor : {};
  const out = { ...next };
  const donorPts = Array.isArray(d.fit_points) ? d.fit_points : [];
  const nextPts = Array.isArray(out.fit_points) ? out.fit_points : [];
  const fitPts = nextPts.length >= 3 ? nextPts : donorPts.length >= 3 ? donorPts : nextPts.length ? nextPts : donorPts;
  if (fitPts.length) out.fit_points = fitPts;

  const diagDonor = d.diagnostics && typeof d.diagnostics === "object" ? d.diagnostics : {};
  const diagNext = out.diagnostics && typeof out.diagnostics === "object" ? out.diagnostics : {};
  const diag = { ...diagDonor, ...diagNext };
  const prefer = (key) => {
    if (_defined(diagNext[key])) return diagNext[key];
    if (_defined(diagDonor[key])) return diagDonor[key];
    return diag[key];
  };
  for (const k of ["r2", "mae_vu", "rmse_vu", "mdape_pct", "r2_source", "fit_points"]) {
    const v = prefer(k);
    if (_defined(v)) diag[k] = v;
  }
  if ((!Array.isArray(diag.fit_points) || diag.fit_points.length < 3) && fitPts.length >= 3) {
    diag.fit_points = fitPts;
  }
  if (diag.r2 == null && fitPts.length >= 3) {
    const m = fitSampleMetricsFromPoints(fitPts);
    if (m?.r2 != null) {
      diag.r2 = m.r2;
      if (diag.mae_vu == null) diag.mae_vu = m.mae_vu;
      if (diag.rmse_vu == null) diag.rmse_vu = m.rmse_vu;
      if (diag.mdape_pct == null) diag.mdape_pct = m.mdape_pct;
      if (!diag.r2_source) diag.r2_source = m.source;
    }
  }
  out.diagnostics = diag;
  const r2Top = out.r2 ?? d.r2 ?? diag.r2;
  if (r2Top != null && Number.isFinite(Number(r2Top))) out.r2 = Number(r2Top);
  return out;
}

/** Categoria de mercado no lab (não é o motor oficial). */
export const LAB_ALERTA_PRIME_BAIRROS = Object.freeze([
  "Moema",
  "Pinheiros",
  "Itaim Bibi",
  "Jardim Paulista",
  "Jardins",
  "Bela Vista",
  "Vila Olímpia",
  "Higienópolis",
  "Morumbi",
  "Cerqueira César",
  "Jardim América",
  "Vila Nova Conceição"
]);

const LAB_GENERIC_BAIRRO_RE = /^(s[aã]o\s*paulo|sp|capital|centro|centro\s*sp|_sem_bairro)$/i;

export function isLabGenericBairro(name) {
  const s = String(name || "").trim();
  if (!s || s === "-" || s.toLowerCase() === "null") return true;
  return LAB_GENERIC_BAIRRO_RE.test(s);
}

export function classifyLabStratum(bairro, hygieneStatus) {
  if (hygieneStatus === "skip_review" || isLabGenericBairro(bairro)) return "revisao";
  if (LAB_ALERTA_PRIME_BAIRROS.includes(String(bairro || "").trim())) return "alerta_prime";
  return "padrao";
}

export const LAB_STRATUM_COPY = Object.freeze({
  padrao: { label: "Mercado padrão", hint: "Amostra urbana típica. O modelo local costuma ser mais estável." },
  alerta_prime: {
    label: "Alerta — alta variação",
    hint: "Bairro com muita diferença de reforma e padrão. O valor é uma faixa, não um ponto único."
  },
  revisao: {
    label: "Revisar bairro",
    hint: "O cadastro veio genérico (cidade ou Centro). Confira o pin antes de emitir."
  }
});

/** Cartões do painel POI — allowlist do cliente (não é o overlay OSM do mapa). */
export const CLIENT_POI_PANEL_ROWS = Object.freeze([
  { key: "ibirapuera", emoji: "🌳", label: "Parque Ibirapuera", distKey: "dist_ibirapuera_m" },
  { key: "villa_lobos", emoji: "🌳", label: "Parque Villa-Lobos", distKey: "dist_villa_lobos_m" },
  {
    key: "trilho",
    emoji: "🚇",
    label: "Trilho mais próximo",
    distKey: "dist_trilho_min_m",
    nameKey: "nearest_trilho_nome",
    modeKey: "nearest_trilho_mode"
  }
]);

export function pickClientPoiDistances(...sources) {
  const out = {};
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    const poi = src.poi && typeof src.poi === "object" ? { ...src, ...src.poi, ...(src.poi.allowlist || {}) } : src;
    const allow = poi.allowlist && typeof poi.allowlist === "object" ? poi.allowlist : {};
    const nestedPois = poi.pois && typeof poi.pois === "object" ? poi.pois : {};
    const fromSubjectPois =
      poi.subject_pois && typeof poi.subject_pois === "object" ? poi.subject_pois : {};
    const nestedAllow = {
      ...(nestedPois.allowlist && typeof nestedPois.allowlist === "object" ? nestedPois.allowlist : {}),
      ...(fromSubjectPois.allowlist && typeof fromSubjectPois.allowlist === "object"
        ? fromSubjectPois.allowlist
        : {})
    };
    const merged = { ...poi, ...nestedPois, ...fromSubjectPois, ...nestedAllow, ...allow };
    for (const row of CLIENT_POI_PANEL_ROWS) {
      const raw = merged[row.distKey];
      if (out[row.distKey] == null && raw != null && raw !== "") {
        const n = Number(raw);
        if (Number.isFinite(n)) out[row.distKey] = n;
      }
      if (row.nameKey && out[row.nameKey] == null && merged[row.nameKey]) {
        out[row.nameKey] = merged[row.nameKey];
      }
      if (row.modeKey && out[row.modeKey] == null && merged[row.modeKey]) {
        out[row.modeKey] = merged[row.modeKey];
      }
    }
  }
  return out;
}

export function formatPoiDistanceM(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(".", ",")} km`;
  return `${Math.round(n)} m`;
}

function haversineM(lat1, lon1, lat2, lon2) {
  const r = 6371000;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dphi = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dphi / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Mesmo Haversine da allowlist / motor V3 — no pin, sem clique Enriquecer. */
export function poiDistancesFromLatLng(latitude, longitude, catalog = poiAllowlistSpCapital) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return {};
  const parks = Array.isArray(catalog?.parks) ? catalog.parks : [];
  const stations = Array.isArray(catalog?.stations) ? catalog.stations : [];
  const ibi = parks.find((p) => p.id === "ibirapuera");
  const vl = parks.find((p) => p.id === "villa_lobos");
  const out = {};
  if (ibi) out.dist_ibirapuera_m = Math.round(haversineM(lat, lng, Number(ibi.lat), Number(ibi.lng)) * 10) / 10;
  if (vl) out.dist_villa_lobos_m = Math.round(haversineM(lat, lng, Number(vl.lat), Number(vl.lng)) * 10) / 10;
  let best = null;
  let bestD = Infinity;
  for (const s of stations) {
    const d = haversineM(lat, lng, Number(s.lat), Number(s.lng));
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  if (best) {
    out.dist_trilho_min_m = Math.round(bestD * 10) / 10;
    out.nearest_trilho_nome = best.name || best.nome || null;
    out.nearest_trilho_mode = best.mode || null;
  }
  return out;
}

/** Barra visual 0–6 km (só UI; o motor usa log da distância real, sem tecto). */
export function poiProximityPct(raw, capM = 6000) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  const cap = Number(capM) > 0 ? Number(capM) : 6000;
  return Math.max(0, Math.min(100, Math.round((1 - Math.min(n, cap) / cap) * 100)));
}

/** Cópia lab: 500 m é o default do wizard oficial — não repor o raio V3. */
export function resolveLabCopyRadiusM(saved) {
  const n = Number(saved);
  if (!Number.isFinite(n) || n <= 0 || n === 500) {
    return AUTOMATIC_LAB_COPY_MOTOR.defaultRadiusM;
  }
  return n;
}

export function pickClientIbge(...sources) {
  const out = {};
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    const b = src.ibge && typeof src.ibge === "object" ? { ...src, ...src.ibge } : src;
    const renda = Number(
      b.renda_responsavel_media ?? b.renda_raw ?? b.renda_media ?? b.renda_per_capita_estimada
    );
    if (out.renda == null && Number.isFinite(renda) && renda > 0) out.renda = renda;
    const setor = b.cd_setor || b.ibge_setor_id;
    if (!out.setor && setor) out.setor = String(setor);
    const pct = Number(b.percentil_renda_sp ?? b.percentil);
    if (out.percentil == null && Number.isFinite(pct)) out.percentil = pct;
    if (b.renda_missing === 0 || b.renda_missing === 1) out.rendaMissing = Number(b.renda_missing);
  }
  return out;
}

function _defined(v) {
  return v != null && v !== "";
}

/** Painel: o snapshot do draft é magro e tapava o result completo do run (R², oferta, etc.). */
export function mergeLabDashboardResult(...objs) {
  const out = {};
  for (const o of objs) {
    if (!o || typeof o !== "object") continue;
    for (const [k, v] of Object.entries(o)) {
      if (!_defined(v)) continue;
      // fit_points: [] vazio é «definido» mas bloqueava métricas melhores — ignorar.
      if (k === "fit_points" && Array.isArray(v) && v.length === 0) continue;
      if (k === "diagnostics" && typeof v === "object") {
        const merged = { ...(out.diagnostics || {}) };
        for (const [dk, dv] of Object.entries(v)) {
          if (!_defined(dv)) continue;
          if (merged[dk] == null) merged[dk] = dv;
        }
        out.diagnostics = merged;
        continue;
      }
      if (out[k] == null) out[k] = v;
    }
  }
  if (out.r2 == null && out.diagnostics && out.diagnostics.r2 != null) out.r2 = out.diagnostics.r2;
  return out;
}

export function collectComparableVus(comps, selectedIds) {
  const pick = (restrict) => {
    const idSet = restrict
      ? new Set((selectedIds || []).map((x) => Number(x)).filter((n) => Number.isFinite(n)))
      : new Set();
    const vus = [];
    for (const c of comps || []) {
      if (idSet.size && !idSet.has(Number(c.id ?? c.property_id))) continue;
      const d = resolveComparableDisplayFields(c);
      if (d.valorM2 != null && d.valorM2 > 0) vus.push(d.valorM2);
    }
    return vus;
  };
  const strict = pick(true);
  return strict.length >= 2 ? strict : pick(false);
}

export function intervalFromComparableVus(comps, selectedIds, subjectArea) {
  const area = Number(subjectArea);
  if (!Number.isFinite(area) || area <= 0) return null;
  const vus = collectComparableVus(comps, selectedIds);
  if (vus.length < 5) return null;
  vus.sort((x, y) => x - y);
  const at = (p) => vus[Math.min(vus.length - 1, Math.max(0, Math.floor((p / 100) * (vus.length - 1))))];
  return {
    min: at(10) * area,
    max: at(90) * area,
    source: "sample_vu_p10_p90"
  };
}

/** Chip IC da etapa de amostra — nunca String(object) → "[object Object]". */
export function formatIcChip(ic) {
  if (ic == null || ic === "") return "—";
  if (typeof ic !== "object") {
    const s = String(ic);
    return s === "[object Object]" ? "—" : s;
  }
  const lowRaw = ic.low;
  const highRaw = ic.high;
  if (lowRaw == null && highRaw == null) return "—";
  const low = Number(lowRaw);
  const high = Number(highRaw);
  const hasLow = lowRaw != null && Number.isFinite(low);
  const hasHigh = highRaw != null && Number.isFinite(high);
  if (!hasLow && !hasHigh) return "—";
  const fmt = (n) =>
    Number(n).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0
    });
  if (hasLow && hasHigh) return `${fmt(low)} – ${fmt(high)}`;
  if (hasLow) return `≥ ${fmt(low)}`;
  return `≤ ${fmt(high)}`;
}

/** MAE em R$/m² dos vizinhos vs VU previsto — legado OLS/wizard; preferir fitSampleMetricsFromPoints no clone V6. */
export function maeVuFromComparables(comps, selectedIds, predictedVu) {
  const hat = Number(predictedVu);
  if (!Number.isFinite(hat) || hat <= 0) return null;
  const vus = collectComparableVus(comps, selectedIds);
  if (vus.length < 2) return null;
  return vus.reduce((s, v) => s + Math.abs(v - hat), 0) / vus.length;
}

/**
 * R² / MAE / RMSE / MdAPE in-sample a partir de fit_points (vu_obs × vu_hat).
 * Mesma lógica do PDF Completo (RQ-126B) — não altera o valor da avaliação.
 */
export function fitSampleMetricsFromPoints(points) {
  const pairs = [];
  for (const p of points || []) {
    const o = Number(p?.vu_obs);
    const h = Number(p?.vu_hat);
    if (!Number.isFinite(o) || !Number.isFinite(h)) continue;
    pairs.push([o, h]);
  }
  const n = pairs.length;
  if (n < 3) return null;
  const meanO = pairs.reduce((s, [o]) => s + o, 0) / n;
  let ssRes = 0;
  let ssTot = 0;
  let absSum = 0;
  const apes = [];
  for (const [o, h] of pairs) {
    const d = o - h;
    ssRes += d * d;
    ssTot += (o - meanO) * (o - meanO);
    absSum += Math.abs(d);
    if (o > 0) apes.push((Math.abs(d) / o) * 100);
  }
  apes.sort((a, b) => a - b);
  let mdape = null;
  if (apes.length) {
    const mid = Math.floor(apes.length / 2);
    mdape = apes.length % 2 ? apes[mid] : (apes[mid - 1] + apes[mid]) / 2;
  }
  return {
    n,
    r2: ssTot > 1e-12 ? 1 - ssRes / ssTot : null,
    mae_vu: absSum / n,
    rmse_vu: Math.sqrt(ssRes / n),
    mdape_pct: mdape,
    source: "fit_points_insample"
  };
}

export const AUTOMATIC_ONLY_USERNAMES = Object.freeze([
  "userteste",
  "userteste@avm.local",
  "user teste"
]);

export function isAutomaticOnlyUser(user) {
  if (!user || typeof user !== "object") return false;
  const uname = String(user.username || "").trim().toLowerCase();
  const email = String(user.email || "").trim().toLowerCase();
  const fname = String(user.full_name || "").trim().toLowerCase();
  const cid = String(user.client_id || "").trim().toLowerCase();
  return Boolean(
    (uname && AUTOMATIC_ONLY_USERNAMES.includes(uname)) ||
    (email && AUTOMATIC_ONLY_USERNAMES.includes(email)) ||
    (fname && AUTOMATIC_ONLY_USERNAMES.includes(fname)) ||
    cid === "pilot_automatic_only"
  );
}

export function canAccessAutomaticCloneRq117(user) {
  if (!AUTOMATIC_CLONE_RQ117_LAB_ENABLED) return false;
  return isPrivilegedAdmin(user) || isAutomaticOnlyUser(user);
}

/** Etapa 6 da cópia: política do modelo (0/5/10) após o cálculo — RQ-129. */
export const LAB_STEP6_COPY = Object.freeze({
  title: "Política do modelo",
  chipLabel: "Política",
  short: "Política 0/5/10",
  subtitle:
    "A estimativa já foi calculada na amostra. Escolha a política 0%, 5% ou 10% aplicada ao resultado do modelo e avance para o resultado.",
  tipTitle: "Etapa 6 · Política do modelo",
  tipBody:
    "Política selecionada sobre o resultado calculado pelo modelo. Não altera comparáveis, treinamento nem P10–P90. Continuar abre o resultado."
});

/** Etapa 8 da cópia: emissão. O rodapé não avança — os PDFs ligam com evaluation_id. */
export const LAB_STEP8_COPY = Object.freeze({
  cta: "Relatórios nesta página",
  short: "PDF e PTAM nesta página",
  hintNoEval:
    "Toque em Preparar laudo e depois em Gerar PDF Completo. Não é um cálculo novo.",
  hintReady: "Última etapa. Gere o PDF Completo — o rodapé não avança mais."
});

export function buildLabStep8CtaHint({ evaluationId, runId } = {}) {
  if (evaluationId) return LAB_STEP8_COPY.hintReady;
  if (runId) return LAB_STEP8_COPY.hintNoEval;
  return LAB_STEP8_COPY.hintNoEval;
}

export const LAB_SPATIAL_STEP_META = Object.freeze(
  SPATIAL_STEP_META.map((s) => {
    if (s.id === 6) {
      return { ...s, label: LAB_STEP6_COPY.chipLabel, short: LAB_STEP6_COPY.short, cta: "Continuar" };
    }
    if (s.id === 8) {
      return { ...s, cta: LAB_STEP8_COPY.cta, short: LAB_STEP8_COPY.short };
    }
    return s;
  })
);

export const LAB_TIMELINE_STEPS = Object.freeze(
  TIMELINE_STEPS.map((s) =>
    s.id === "regressao" ? { ...s, label: LAB_STEP6_COPY.chipLabel } : s
  )
);

export function isLabGeocodeGateMessage(text) {
  return /geocode\s*fraco/i.test(String(text || ""));
}

const LAB_CLIENT_NOISE_RE =
  /homolog|nbr|bancár|sisaval|sintétic|tester\/qa|uko|mvp|b3\/poi|ml \/predict|challenger|checksum|laborat|cópia|ibge_poi|não entram|não aplicado|pois indispon|ibge\/renda ausente|certifica|conformidade|precisão global|baseline científico/i;

export function isLabClientNoiseText(text) {
  return LAB_CLIENT_NOISE_RE.test(String(text || ""));
}

/** Avisos de ecrã: some gate interno e texto que não deve ir a cliente. */
export function filterLabOperatorAlerts(alerts) {
  return (Array.isArray(alerts) ? alerts : []).filter(
    (a) => !isLabGeocodeGateMessage(a) && !isLabClientNoiseText(a)
  );
}

export function labClientLimitations() {
  return [LAB_CLIENT_COPY.disclaimer];
}

/**
 * Factos do run para a etapa 7. Não inventa fatias em R$/%
 * (o fallback 55/25/10/5 de groupExplainableContributions não é o OLS).
 * @param {object} extras.enrichment — spatialEnrichment do draft (IBGE/POI).
 */
export function buildLabResultFacts(result = {}, subject = {}, extras = {}) {
  const enrichment = extras.enrichment || null;
  const contribs =
    result.feature_contributions || result.explainability?.feature_contributions || [];
  const hasRealContribs = Array.isArray(contribs) && contribs.length > 0;
  const offer = result.offer_factor || {};
  const offerPct =
    offer.percent != null
      ? Number(offer.percent)
      : offer.rate != null
        ? Number(offer.rate) * 100
        : null;
  const mv = String(result.model_version || "").toLowerCase();
  // RQ-127 — clone c1: oferta no valor final; elasticidade comercial removida.
  const offerOnFinal =
    String(offer.mode || "") === "final_value" || mv.includes("ens_8515_c1") || mv.includes("_c1");
  const fitPts = result.fit_points || result.diagnostics?.fit_points || [];
  const fitM = fitSampleMetricsFromPoints(fitPts);
  const diag = result.diagnostics || {};
  const r2Raw = diag.r2 ?? result.r2 ?? fitM?.r2 ?? null;
  const maeRaw = diag.mae_vu ?? fitM?.mae_vu ?? null;
  const rmseRaw = diag.rmse_vu ?? diag.rmse ?? fitM?.rmse_vu ?? null;
  const mdapeRaw = diag.mdape_pct ?? fitM?.mdape_pct ?? null;
  const r2Source = diag.r2_source || (fitM?.source ? fitM.source : null);
  const hygiene =
    result.explainability?.bairro_hygiene ||
    diag.challenger?.bairro_hygiene ||
    {};
  const bairro = hygiene.bairro || subject?.bairro || subject?.neighborhood || "";
  const stratum =
    result.explainability?.lab_stratum ||
    diag.challenger?.lab_stratum ||
    classifyLabStratum(bairro, hygiene.status);
  const arb = result.arbitration || null;
  const arbPct =
    arb && arb.percent != null && Number.isFinite(Number(arb.percent))
      ? Number(arb.percent) * 100
      : null;

  const ibge = pickClientIbge(
    result.explainability?.subject_ibge,
    result.pipeline_features,
    result.pipeline_features?.ibge,
    enrichment?.subject_ibge,
    subject?.ibge
  );
  const poi = pickClientPoiDistances(
    poiDistancesFromLatLng(subject?.latitude ?? subject?.lat, subject?.longitude ?? subject?.lng),
    result.explainability?.subject_poi,
    result.pipeline_features,
    enrichment?.subject_pois,
    enrichment?.subject_poi,
    subject?.poi
  );

  const valorFinal =
    result.valor_total != null
      ? Number(result.valor_total)
      : result.valor_central_arbitrado != null
        ? Number(result.valor_central_arbitrado)
        : result.valor_central != null
          ? Number(result.valor_central)
          : null;
  const valorModelo =
    result.valor_modelo != null
      ? Number(result.valor_modelo)
      : result.valor_total_bruto != null
        ? Number(result.valor_total_bruto)
        : null;
  const valorPreArb =
    result.valor_pre_arbitrio != null ? Number(result.valor_pre_arbitrio) : null;
  const vu =
    result.vu_ajustado != null
      ? Number(result.vu_ajustado)
      : result.valor_unitario != null
        ? Number(result.valor_unitario)
        : null;
  const vuBruto =
    result.vu_bruto != null && Number.isFinite(Number(result.vu_bruto))
      ? Number(result.vu_bruto)
      : result.vu_modelo != null && Number.isFinite(Number(result.vu_modelo))
        ? Number(result.vu_modelo)
        : null;

  // RQ-133 — origem dos campos P10/P90 (só leitura; sem recalcular no FE):
  // - valor_min/max: API (P10/P90 de sample_vus × área) · interval_source=sample_vu_p10_p90
  // - vu_band_meta: diagnostics.challenger (p10/p50/p90 em R$/m²) se o motor enviou
  // - sample_vus: raramente no result FE (API usa internamente; não inventar)
  // - fit_points[].vu_obs: VU observados dos comps do fit (histograma, se sample_vus ausente)
  const vuBandMetaRaw =
    diag.challenger?.vu_band_meta ||
    result.vu_band_meta ||
    result.explainability?.vu_band_meta ||
    null;
  const vuBandMeta =
    vuBandMetaRaw && typeof vuBandMetaRaw === "object"
      ? {
          p10:
            vuBandMetaRaw.p10 != null && Number.isFinite(Number(vuBandMetaRaw.p10))
              ? Number(vuBandMetaRaw.p10)
              : null,
          p50:
            vuBandMetaRaw.p50 != null && Number.isFinite(Number(vuBandMetaRaw.p50))
              ? Number(vuBandMetaRaw.p50)
              : null,
          p90:
            vuBandMetaRaw.p90 != null && Number.isFinite(Number(vuBandMetaRaw.p90))
              ? Number(vuBandMetaRaw.p90)
              : null,
          n:
            vuBandMetaRaw.n != null && Number.isFinite(Number(vuBandMetaRaw.n))
              ? Number(vuBandMetaRaw.n)
              : null
        }
      : null;
  const sampleVusRaw =
    result.sample_vus ||
    diag.sample_vus ||
    diag.challenger?.sample_vus ||
    null;
  const sampleVus = Array.isArray(sampleVusRaw)
    ? sampleVusRaw.map(Number).filter((v) => Number.isFinite(v) && v > 0)
    : [];
  const subjectAreaRaw =
    subject?.areaPrivativa ?? subject?.area_privativa ?? subject?.area ?? null;
  const subjectArea =
    subjectAreaRaw != null && Number.isFinite(Number(subjectAreaRaw)) && Number(subjectAreaRaw) > 0
      ? Number(subjectAreaRaw)
      : null;

  const nUsed = Array.isArray(result.comparables_used) ? result.comparables_used.length : null;
  const eligible =
    diag.n_eligible_before_cap ??
    diag.filter_trail?.n_iqr ??
    null;
  const nRefused = Array.isArray(result.comparables_refused)
    ? result.comparables_refused.length
    : null;
  const hp =
    result.hp_meta ||
    diag.hp_meta ||
    diag.challenger?.hp_meta ||
    result.explainability?.hp_meta ||
    null;
  const nAfterIqr =
    hp?.n_after_iqr != null && Number.isFinite(Number(hp.n_after_iqr))
      ? Number(hp.n_after_iqr)
      : null;
  const nFitMeta =
    hp?.n_fit != null && Number.isFinite(Number(hp.n_fit)) ? Number(hp.n_fit) : null;
  const radiusM =
    hp?.radius_m != null && Number.isFinite(Number(hp.radius_m))
      ? Number(hp.radius_m)
      : result.diagnostics?.radius_m != null
        ? Number(result.diagnostics.radius_m)
        : null;
  const kCapRaw = hp?.k != null && Number.isFinite(Number(hp.k)) ? Number(hp.k) : null;
  const kCap =
    kCapRaw != null
      ? kCapRaw
      : mv.includes("ens_8515") || mv.includes("v6")
        ? 300
        : null;

  const valorMin =
    result.valor_min != null && Number.isFinite(Number(result.valor_min))
      ? Number(result.valor_min)
      : diag.ic?.low != null && Number.isFinite(Number(diag.ic.low))
        ? Number(diag.ic.low)
        : null;
  const valorMax =
    result.valor_max != null && Number.isFinite(Number(result.valor_max))
      ? Number(result.valor_max)
      : diag.ic?.high != null && Number.isFinite(Number(diag.ic.high))
        ? Number(diag.ic.high)
        : null;
  const intervalSource = diag.interval_source || null;

  const varsUsed = Array.isArray(result.variables_used)
    ? result.variables_used.map(String).filter(Boolean)
    : [];
  const geosampaInX = !!(
    result.explainability?.geosampa_in_x ||
    result.geosampa_in_x ||
    diag.challenger?.geosampa_in_x
  );

  return {
    hasRealContribs,
    nUsed,
    nEligible: eligible != null && Number.isFinite(Number(eligible)) ? Number(eligible) : null,
    nRefused: nRefused != null && nRefused > 0 ? nRefused : nRefused === 0 ? 0 : null,
    nAfterIqr,
    nFitMeta,
    radiusM: radiusM != null && Number.isFinite(radiusM) ? radiusM : null,
    kCap,
    r2: r2Raw != null && Number.isFinite(Number(r2Raw)) ? Number(r2Raw) : null,
    maeVu: maeRaw != null && Number.isFinite(Number(maeRaw)) ? Number(maeRaw) : null,
    rmseVu: rmseRaw != null && Number.isFinite(Number(rmseRaw)) ? Number(rmseRaw) : null,
    mdapePct: mdapeRaw != null && Number.isFinite(Number(mdapeRaw)) ? Number(mdapeRaw) : null,
    r2Source: r2Source || null,
    fitPoints: Array.isArray(fitPts) ? fitPts : [],
    offerPct: Number.isFinite(offerPct) ? offerPct : null,
    offerOnFinal,
    offerApplied: !!offer.applied || (Number.isFinite(offerPct) && offerPct > 0),
    elasticityApplied: false,
    elasticityPct: null,
    subjectFactsLine: formatLabSubjectFactsLine(subject),
    subject,
    arbitrationApplied: !!(arb && (arb.replaces_prediction || arb.percent != null)),
    arbitrationPct: arbPct,
    arbitrationJust: arb?.justification ? String(arb.justification).trim() : null,
    ibge,
    poi,
    ibgeInMotor: ibge.renda != null || !!(
      result.pipeline_features?.ibge ||
      result.explainability?.subject_ibge ||
      subject?.ibge
    ),
    poiInMotor:
      poi.dist_ibirapuera_m != null ||
      poi.dist_villa_lobos_m != null ||
      poi.dist_trilho_min_m != null ||
      !!(
        result.pipeline_features?.poi ||
        result.explainability?.subject_poi ||
        result.pipeline_features?.subject_poi
      ),
    valorFinal: valorFinal != null && Number.isFinite(valorFinal) ? valorFinal : null,
    valorModelo: valorModelo != null && Number.isFinite(valorModelo) ? valorModelo : null,
    valorPreArb: valorPreArb != null && Number.isFinite(valorPreArb) ? valorPreArb : null,
    vu: vu != null && Number.isFinite(vu) ? vu : null,
    vuBruto: vuBruto != null && Number.isFinite(vuBruto) ? vuBruto : null,
    subjectArea,
    sampleVus,
    vuBandMeta,
    valorMin,
    valorMax,
    intervalSource,
    variablesUsed: varsUsed,
    geosampaInX,
    motor: result.model_version || AUTOMATIC_LAB_COPY_MOTOR.modelVersion,
    labStratum: stratum,
    labStratumCopy: LAB_STRATUM_COPY[stratum] || LAB_STRATUM_COPY.padrao,
    bairroResolved: bairro || null,
    hygieneStatus: hygiene.status || null
  };
}

/** Ordem do bloco NBR no drawer. Incluir a cópia aqui ou ela não pinta (RQ-117). */
export function collectNbrDrawerItems({
  sisavalStudioItem = null,
  valuationEnterpriseItem = null,
  valuationAutomaticItem = null,
  automaticLabCopyItem = null,
  workspaceV2Item = null,
  nbrLabItem = null
} = {}) {
  return [
    sisavalStudioItem,
    valuationEnterpriseItem,
    valuationAutomaticItem,
    automaticLabCopyItem,
    workspaceV2Item,
    nbrLabItem
  ].filter(Boolean);
}
